import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../services/api";
import "./EditRecord.css";

const EditRecordPage = () => {
  const { id: businessId, recordid: id } = useParams();
  const navigate = useNavigate();
  const [recordData, setRecordData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatedData, setUpdatedData] = useState({});

  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leavePayments, setLeavePayments] = useState({});
  const [updatedLeavePayments, setUpdatedLeavePayments] = useState({});

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const recordResponse = await api.get(`/payroll/record/${businessId}/${id}`);
        const record = recordResponse.data;
        setRecordData(record);
        console.log("✅ Record fetched:", record);

        const { employee_id, start_date, end_date, pay_date } = record;
        if (!employee_id || !start_date || !end_date || !pay_date) {
          console.warn("❌ Missing record fields:", { employee_id, start_date, end_date, pay_date });
          return;
        }

        const leaveRes = await api.get(`/leave-requests/${businessId}/${employee_id}/${start_date}/${end_date}`);
        setLeaveRequests(leaveRes.data);
        console.log("📘 Leave Requests:", leaveRes.data);

        const leavePayRes = await api.get(`/leave-payments/by-employee/${businessId}/${employee_id}/${start_date}/${end_date}`);
        setLeavePayments(leavePayRes.data);
        console.log("💰 Leave Payments:", leavePayRes.data);

        setError(null);
      } catch (err) {
        console.error("❌ fetchAll error:", err);
        setError("Failed to load record and leave data.");
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [businessId, id]);

  const handleInputChange = (field, value) => {
    setUpdatedData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const getSalariedHourlyRate = (salaryPay, frequency) => {
    const weeksPerFrequency = {
      weekly: 1,
      biweekly: 2,
      semimonthly: 2.1667,
      monthly: 4.3333,
    };
    const weeks = weeksPerFrequency[frequency?.toLowerCase()] || 1;
    return (salaryPay / (weeks * 40)).toFixed(2); // 40 hours/week
  };

  const handleLeavePaymentChange = (paymentId, value) => {
    const safeValue = isNaN(value) ? 0 : value;

    setUpdatedLeavePayments((prev) => ({
      ...prev,
      [paymentId]: {
        ...prev[paymentId],
        total_hours_paid: safeValue,
      },
    }));

    const allPayments = [];
    Object.entries(leavePayments).forEach(([leaveId, payments]) => {
      payments.forEach((payment) => {
        const override = (payment.id in updatedLeavePayments) ? updatedLeavePayments[payment.id] : payment;
        allPayments.push({
          ...payment,
          total_hours_paid: (payment.id === paymentId) ? safeValue : override.total_hours_paid
        });
      });
    });

    const totalHours = allPayments.reduce((sum, p) => sum + (parseFloat(p.total_hours_paid) || 0), 0);

    if (parseFloat(recordData.hourly_rate) > 0) {
      setUpdatedData((prev) => ({
        ...prev,
        vacation_hours: totalHours
      }));
    } else if (parseFloat(recordData.salarypay) > 0) {
      const hourlyRate = parseFloat(getSalariedHourlyRate(recordData.salarypay, recordData.frequency));
      const totalVacationPay = +(totalHours * hourlyRate).toFixed(2);
      setUpdatedData((prev) => ({
        ...prev,
        vacation_pay: totalVacationPay,
        salary_vac_hours: totalHours
      }));
    }
  };

  const handleSave = async () => {
    try {
      const allLeavePayments = [];
      Object.entries(leavePayments).forEach(([leaveId, payments]) => {
        payments.forEach((payment) => {
          const override = updatedLeavePayments[payment.id];
          allLeavePayments.push({
            id: payment.id,
            total_hours_paid: override?.total_hours_paid ?? payment.total_hours_paid,
            total_days_paid: override?.total_days_paid ?? payment.total_days_paid,
          });
        });
      });

      const payload = {
        ...recordData,
        ...updatedData,
        leave_payment_updates: allLeavePayments,
      };

      delete payload.grosspay;
      delete payload.net_pay;

      if (
        parseFloat(recordData.hourly_rate) > 0 &&
        (payload.regularhours === null || payload.regularhours === undefined || payload.regularhours === "")
      ) {
        alert("Regular hours cannot be blank for hourly employees.");
        return;
      }

      console.log("🚀 Final Payload to Backend:", payload);

      await api.put(`/payroll/record/${businessId}/${id}`, payload);
      alert("Record updated successfully.");
      navigate(`/dashboard/business/${businessId}/viewpayroll?tab=editlast`);
    } catch (err) {
      console.error("❌ Error saving payroll record:", err);
      alert("Failed to save record. Please try again.");
    }
  };



  if (loading) return <p>Loading data...</p>;
  if (error) return <p>{error}</p>;

  return (
    <div className="edit-record-page">
      <h5>Edit Payroll Record</h5>
      <div>Name: {recordData.name}</div>
      <div>Pay Period: {new Date(recordData.start_date).toLocaleDateString()} to {new Date(recordData.end_date).toLocaleDateString()}</div>
      <div>Pay Date: {new Date(recordData.pay_date).toLocaleDateString()}</div>

      

      <table className="edit-record-table">
  <thead>
    <tr>
      {(recordData.hourly_rate !== null && parseFloat(recordData.hourly_rate) > 0) ? (
        <>
          <th>Regular Hours</th>
          <th>Overtime</th>
          <th>Doubletime</th>
          <th>Vacation Hours</th>
        </>
      ) : (
        <th>Vacation Pay</th>
      )}
      
    </tr>
  </thead>
  <tbody>
    <tr>
      {(recordData.hourly_rate !== null && parseFloat(recordData.hourly_rate) > 0) ? (
        <>
          <td>
            <input
              type="number"
              value={updatedData.regularhours ?? recordData.regularhours ?? ""}
              onChange={(e) => handleInputChange("regularhours", e.target.value)}
            />
          </td>
          <td>
            <input
              type="number"
              value={updatedData.overtimehours ?? recordData.overtimehours ?? ""}
              onChange={(e) => handleInputChange("overtimehours", e.target.value)}
            />
          </td>
          <td>
            <input
              type="number"
              value={updatedData.doubletimehours ?? recordData.doubletimehours ?? ""}
              onChange={(e) => handleInputChange("doubletimehours", e.target.value)}
            />
          </td>
          <td>
            <input
              type="number"
              value={updatedData.vacation_hours ?? recordData.vacation_hours ?? ""}
              onChange={(e) => handleInputChange("vacation_hours", e.target.value)}
            />
          </td>
        </>
      ) : (
        <td>
          <input
            type="number"
            value={updatedData.vacation_pay ?? recordData.vacation_pay ?? ""}
            onChange={(e) => handleInputChange("vacation_pay", e.target.value)}
          />
        </td>
      )}
      </tr>
      <tr>
      <th>Cash Advance</th>
      <th>Bonus</th>
      <th>Tips</th>
      <th>Reimbursement</th>
      </tr>
      <tr>
      <td>
        <input
          type="number"
          value={updatedData.cashadvance ?? recordData.cashadvance ?? ""}
          onChange={(e) => handleInputChange("cashadvance", e.target.value)}
        />
      </td>
      <td>
        <input
          type="number"
          value={updatedData.bonus ?? recordData.bonus ?? ""}
          onChange={(e) => handleInputChange("bonus", e.target.value)}
        />
      </td>
      <td>
        <input
          type="number"
          value={updatedData.tips ?? recordData.tips ?? ""}
          onChange={(e) => handleInputChange("tips", e.target.value)}
        />
      </td>
      <td>
        <input
          type="number"
          value={updatedData.reimbursement ?? recordData.reimbursement ?? ""}
          onChange={(e) => handleInputChange("reimbursement", e.target.value)}
        />
      </td>
    
    </tr>
  </tbody>
</table>


      {leaveRequests.length > 0 && (
        <>
          <div>Leave Requests During This Period</div>
          <table className="edit-leave-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Start - End</th>
                <th>Total Days Requested</th>
                <th>Total Hours Requested</th>
                <th>Total Hours Paid</th>
                <th>Remaining Hours</th>
                <th>Leave Days in Period</th>
              </tr>
            </thead>
            <tbody>
              {leaveRequests.map((leave) => (
                <React.Fragment key={leave.id}>
                  <tr className="edit-request-row">
                    <td>{leave.request_type}</td>
                    <td>{new Date(leave.start_date).toLocaleDateString()} - {new Date(leave.end_date).toLocaleDateString()}</td>
                    <td>{leave.total_days_requested}</td>
                    <td>{leave.total_hours_requested}</td>
                    <td>{leave.total_hours_paid}</td>
                    <td>{leave.total_hours_requested - leave.total_hours_paid}</td>
                    <td>{leave.leave_days_in_period}</td>
                  </tr>

                  {leavePayments[leave.id] && leavePayments[leave.id].map((payment) => (
                    <tr key={payment.id} className="edit-payment-row">
                      <td style={{ fontStyle: "italic" }}>Leave Payment</td>
                      <td>Days Paid - {payment.total_days_paid}</td>
                      <td>
                        Hours Paid - 
                        <input
                          type="number"
                          step="0.01"
                          defaultValue={payment.total_hours_paid}
                          onChange={(e) => handleLeavePaymentChange(payment.id, parseFloat(e.target.value))}
                        />
                      </td>
                      {(recordData.salarypay && recordData.frequency) && (
                        <td>Hourly Rate: ${getSalariedHourlyRate(recordData.salarypay, recordData.frequency)}</td>
                      )}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </>
      )}


      <button className="edit-emp-btn" type="button" onClick={handleSave}>
        Save Changes
      </button>
      <button className="cancel-emp-btn" type="button" onClick={() => navigate(`/dashboard/business/${businessId}/viewpayroll?tab=editlast`)}>
        Cancel
      </button>
    </div>
  );
};

export default EditRecordPage;




